import { Button, Modal, useToastContext } from '@payaca/ambrosia';
import { Deal as Project } from '@payaca/types/dealTypes';
import { PublicHydratedEntityTemplate } from '@payaca/types/entity-templates';
import { Job as Proposal } from '@payaca/types/jobTypesV2';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import useApplyProposalTemplate from '../../../api/mutations/proposals/useApplyProposalTemplate';
import TemplateSelectionControl from '../templateSelectionControl/TemplateSelectionControl';

type ApplyTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  proposal: Proposal;
  project: Project;
};

const ApplyTemplateModal: FC<ApplyTemplateModalProps> = (props) => {
  const { isOpen, onClose, onSuccess, proposal, project } = props;

  const [showConfirmation, setShowConfirmation] = useState(false);

  const [selectedTemplate, setSelectedTemplate] =
    useState<PublicHydratedEntityTemplate<'proposal'>>();

  const { pushError } = useToastContext();

  const { mutateAsync: applyTemplate, isPending: isApplyingTemplate } =
    useApplyProposalTemplate({
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: () => {
        pushError('Failed to apply template. Please try again.');
        onClose();
      },
    });

  useEffect(() => {
    if (isOpen) {
      setShowConfirmation(false);
      setSelectedTemplate(undefined);
    }
  }, [isOpen]);

  const sectionsWhichWillBeOverwritten = useMemo(() => {
    const sectionsWhichWillBeOverwritten = [
      'Proposal type (Quote or Estimate)',
    ];

    if (proposal.jobDescription) {
      sectionsWhichWillBeOverwritten.push('Intro');
    }
    if (proposal.jobNotes) {
      sectionsWhichWillBeOverwritten.push('Notes');
    }
    if (project.paymentSchedule) {
      sectionsWhichWillBeOverwritten.push('Payment schedule');
    }
    if (proposal.dueInOrValidForDays !== undefined) {
      sectionsWhichWillBeOverwritten.push('Valid for (days)');
    }
    if (proposal.summaryMessages && proposal.summaryMessages.length > 0) {
      sectionsWhichWillBeOverwritten.push('Payment summary alert');
    }

    return sectionsWhichWillBeOverwritten;
  }, [proposal, project.paymentSchedule]);

  const handleApply = useCallback(async () => {
    if (!selectedTemplate?.publicId || !proposal.publicId) return;

    await applyTemplate({
      proposalId: proposal.publicId,
      templateId: selectedTemplate.publicId,
    });
  }, [selectedTemplate?.publicId, proposal.publicId, applyTemplate]);

  const handleNext = useCallback(() => {
    if (sectionsWhichWillBeOverwritten.length > 0) {
      setShowConfirmation(true);
    } else {
      void handleApply();
    }
  }, [sectionsWhichWillBeOverwritten, handleApply]);

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      title={
        showConfirmation
          ? `Apply “${selectedTemplate?.name}” Template?`
          : 'Select a Template'
      }
    >
      {!showConfirmation ? (
        <>
          <Modal.Body>
            <TemplateSelectionControl<'proposal'>
              selectedTemplate={selectedTemplate}
              label="Choose a Template"
              onChange={setSelectedTemplate}
              allowedEntityTypes={['proposal']}
            />
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="solid"
                disabled={!selectedTemplate || isApplyingTemplate}
                isProcessing={isApplyingTemplate}
                onClick={handleNext}
              >
                {sectionsWhichWillBeOverwritten.length > 0 ? 'Next' : 'Apply'}
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body>
            <div className="typography:prose space-y-4">
              <p>
                Applying this Template will overwrite the following sections of
                the Proposal:
              </p>
              <ul>
                {sectionsWhichWillBeOverwritten.map((section) => (
                  <li key={section}>{section}</li>
                ))}
              </ul>
              <p>Any existing content in these sections will be replaced.</p>
              <p className="font-bold">Would you like to continue?</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="solid"
                colour="red"
                isProcessing={isApplyingTemplate}
                disabled={isApplyingTemplate}
                onClick={handleApply}
              >
                Overwrite
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ApplyTemplateModal;
